import { Check } from 'lucide-react';
import styled from 'styled-components';
import { key } from 'styled-theme';

const mobileBreakpoint = key('breakpoints.mobile', '480px');

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

export const ModalContainer = styled.div`
  background: white;
  border-radius: 4px;
  width: 95%;
  max-width: 1400px;
  height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;

  /* Mobile only: the map + sidebar stack instead of sitting side-by-side,
     so let the modal use the full viewport instead of a boxed 90vh card. */
  @media (max-width: ${mobileBreakpoint}) {
    width: 100%;
    max-width: 100%;
    height: 100dvh;
    border-radius: 0;
  }
`;

export const ModalHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9fa;
`;

export const ModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #333;
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;
  color: red;

  &:hover {
    background-color: #e0e0e0;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const ModalBody = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;

  /* Mobile only: left/map on top, right/sidebar below — desktop and
     tablet keep the existing side-by-side flex row as-is. */
  @media (max-width: ${mobileBreakpoint}) {
    flex-direction: column;
    overflow-y: auto;
  }
`;

export const MapContainer = styled.div`
  flex: 1;
  position: relative;
  background: #f5f5f5;

  @media (max-width: ${mobileBreakpoint}) {
    flex: none;
    width: 100%;
    height: 45vh;
    min-height: 280px;
  }
`;

export const Sidebar = styled.div`
  width: 320px;
  background: white;
  border-left: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: ${mobileBreakpoint}) {
    width: 100%;
    border-left: none;
    border-top: 1px solid #e0e0e0;
    flex: 1;
    overflow: visible;
  }
`;

export const ProgressSection = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
`;

export const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 12px;
`;

export const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #4caf50, #45a049);
  transition: width 0.3s ease;
  width: ${(props) => props.progress}%;
`;

export const ProgressText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #666;
  text-align: center;
`;

export const CurrentOverlaySection = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
`;

export const OverlayLabel = styled.p`
  margin: 0 0 8px 0;
  font-size: 12px;
  color: #666;
  font-weight: 600;
  text-transform: uppercase;
`;

export const OverlayName = styled.h3`
  margin: 0;
  font-size: 18px;
  color: #333;
  font-weight: 600;
`;

export const CompletedList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;

  /* Mobile: let ModalBody own the scroll instead of nesting a second
     scroll container inside the stacked sidebar. */
  @media (max-width: ${mobileBreakpoint}) {
    flex: none;
    overflow: visible;
  }
`;

export const CompletedItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  background: #f0f9ff;
  border-radius: 6px;
  margin-bottom: 8px;
  border: 1px solid #bae6fd;
`;

export const CheckIcon = styled(Check)`
  color: #0ea5e9;
  margin-right: 12px;
  flex-shrink: 0;
`;

export const CompletedItemText = styled.span`
  font-size: 14px;
  color: #333;
  flex: 1;
`;

export const ButtonContainer = styled.div`
  padding: 20px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  gap: 12px;
`;

export const Button = styled.button`
  flex: 1;
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const CaptureButton = styled(Button)`
  background: linear-gradient(90deg, #7b5cff, #60a5fa);
  color: white;
  font-family: 'JetBrains Mono Regular';

  &:hover:not(:disabled) {
    background: linear-gradient(90deg, #7b5cff, #60a5fa);
    transform: translateY(-1px);
  }
`;

export const CompleteButton = styled(Button)`
  background: #4caf50;
  color: white;
  font-family: 'JetBrains Mono Regular';

  &:hover:not(:disabled) {
    background: #45a049;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
  }
`;

export const ErrorMessage = styled.div`
  margin: 16px 20px;
  padding: 12px;
  background: #ffebee;
  border: 1px solid #ef5350;
  border-radius: 6px;
  color: #c62828;
  font-size: 14px;
`;
